import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';

import AlertBanner from '~/components/core/AlertBanner';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import InnerCard from '~/components/core/Cards/InnerCard';
import InvolvedInsurerFields from '~/components/Fnol/NewFnolUI/InvolvedParties/InvolvedInsurerFields';
import { useIncidentConfiguration } from '~/components/hooks/useIncidentConfiguration';
import useIsConfigurationFieldSupportedByCategories from '~/components/hooks/useIsConfigurationFieldSupportedByCategories';
import { useLob } from '~/components/hooks/useLob';
import { getManagementPreviewLabelSuffix, resolveCoveredObjectFields } from '~/Utils/managementPreviewUtils';

import { getCoveredObjectDisplayName } from '../../../../../Utils';
import { ContactShowOnlyTextField } from '../../../../ContactTextFieldFormik';
import useCurrencyFormatter from '../../../../CurrencyFormatterContext';
import ConfiguredFields, {
  CustomField,
  FULL_ROW_TYPES,
  PreDefinedField,
  prepareConfiguredFields,
} from '../../../../IncidentConfiguration/ConfiguredFields';
import { CustomFieldsContextProvider } from '../../../../IncidentConfiguration/CustomFieldsContext';
import { usePolicy } from '../../../../PolicyContainer';
import TextFieldFormik from '../../../../TextFieldFormik';

import { getEligibleCoveredObjects, NOT_LISTED_ON_POLICY_COVERED_OBJECT_ID } from './coveredObjectsUtils';
import { ENTITIES_TYPES } from './InvolvedGenericFragment';

import styles from './index.module.scss';

// Custom hook to get covered object configuration for an involved party
const useInvolvedPartyCoveredObjectConfig = (involvedParty) => {
  const { lob } = useLob();
  const { incidentConfiguration } = useIncidentConfiguration();
  const { policy } = usePolicy();

  const { coveredObjects, coveredObjectType, coveredObjectDesc, isPolicyListed, allowsNotListedOnPolicy } =
    getEligibleCoveredObjects(involvedParty, lob, policy, incidentConfiguration);

  return {
    coveredObjects,
    coveredObjectType,
    coveredObjectDesc,
    isPolicyListed,
    allowsNotListedOnPolicy,
  };
};

// Contact-typed configured fields need both id and full_name pulled from the
// configurable_contact_<N> slot on the covered object. Other types route through CustomField.
const ReadOnlyCoveredObjectField = ({ field, linkedCoveredObject }) => {
  if (field?.type === 'contact') {
    return (
      <ContactShowOnlyTextField
        label={field.desc}
        contactId={linkedCoveredObject?.[`${field.db_field}_id`] ?? ''}
        contactDisplayName={linkedCoveredObject?.[`${field.db_field}_full_name`] ?? ''}
      />
    );
  }
  const value = (linkedCoveredObject?.configured_fields_values || {})[field.id];
  return <CustomField field={field} value={value} readOnly inline={false} />;
};

ReadOnlyCoveredObjectField.propTypes = {
  field: PropTypes.object.isRequired,
  linkedCoveredObject: PropTypes.object,
};

const GeneralDetailsFragment = ({ involvedParty, preDefinedFields }) => {
  const { policy } = usePolicy();
  const { incidentConfiguration } = useIncidentConfiguration();
  const { isFieldSupportedByCategories } = useIsConfigurationFieldSupportedByCategories();
  const { currencyFormatter } = useCurrencyFormatter();
  const { values, handleChange, setFieldValue, initialValues } = useFormikContext();
  const involvedPartiesConfig = incidentConfiguration?.involved_parties || {};
  const involvedPartyConfig = involvedPartiesConfig[involvedParty];
  const partyType = involvedParty && involvedPartyConfig?.type;
  const isFirstParty = involvedParty && involvedPartyConfig?.is_first_party;

  // Use the custom hook to get covered object configuration
  const { coveredObjects, coveredObjectDesc, isPolicyListed, allowsNotListedOnPolicy } =
    useInvolvedPartyCoveredObjectConfig(involvedParty);

  const isManualPolicyOnFnol = policy.is_manual; // is_manual - appears only on fnol
  const isScheduledFirstParty = isFirstParty && isPolicyListed;
  // A first party's contact comes from the covered-object dropdown only when that dropdown is shown
  // (scheduled first party on a non-manual policy). Otherwise — third party, no/manual policy, or an
  // unscheduled first party — the contact picker must be shown instead.
  const showsCoveredObjectDropdown = Boolean(policy && isScheduledFirstParty && !isManualPolicyOnFnol);
  // The sentinel only exists in flight: once the claim is created the party carries the real id of a
  // covered object the claim owns, which is what is_covered_object_not_listed_on_policy reports (the
  // claim page renders this same fragment, seeded from the involved detail payload).
  const isMarkedNotListedOnPolicy =
    values.covered_object_id === NOT_LISTED_ON_POLICY_COVERED_OBJECT_ID ||
    Boolean(values.is_covered_object_not_listed_on_policy);
  // Offered whenever it is already selected, not only while the covered object type's Policy
  // Configuration setting allows it — mirroring the server's _link_not_listed_covered_object, which
  // keeps the already-owned case outside the setting's check so a claim stays workable after it is
  // switched back off.
  const showsNotListedOnPolicyOption =
    showsCoveredObjectDropdown && (allowsNotListedOnPolicy || isMarkedNotListedOnPolicy);
  // Match the option's value to whatever the field already holds, so the Select can resolve it: a
  // persisted claim-owned object matches no MenuItem built from policy.covered_objects, which excludes
  // policy_id NULL, and MUI renders a blank Select. Guarded on isMarkedNotListedOnPolicy so that for a
  // policy-listed party this stays the sentinel rather than colliding with a real object's MenuItem.
  const notListedOptionValue =
    isMarkedNotListedOnPolicy && values.covered_object_id
      ? values.covered_object_id
      : NOT_LISTED_ON_POLICY_COVERED_OBJECT_ID;
  // An empty policy is no longer a dead end once that option exists, so the "nothing to pick"
  // disabling and warning have to stand down for it.
  const hasNothingToSelect = isEmpty(coveredObjects) && !showsNotListedOnPolicyOption;
  const showsManualCoveredObjectEntry = !showsCoveredObjectDropdown || isMarkedNotListedOnPolicy;
  const shouldShowPropertyDescription =
    isEmpty(coveredObjects) ||
    !isFirstParty ||
    isMarkedNotListedOnPolicy ||
    (initialValues.property_description && !values.covered_object_id);

  const configuredFields = prepareConfiguredFields(
    involvedPartyConfig?.configured_fields,
    isFieldSupportedByCategories
  );

  const { linkedCoveredObject, fields: coveredObjectShowInInvolvedFields } = React.useMemo(
    () => resolveCoveredObjectFields(policy, incidentConfiguration, values.covered_object_id, 'show_in_involved'),
    [policy, incidentConfiguration, values.covered_object_id]
  );

  return (
    <Grid container className={styles.involvedContainer}>
      <InnerCard title="General Details" className={styles.innerCard} titleClassName={styles.genericTitle}>
        <Grid container spacing={1}>
          {showsCoveredObjectDropdown && (
            <Grid item xs={12}>
              <TextFieldFormik
                id="covered_object_id"
                label={`Select ${coveredObjectDesc}`}
                select
                fullWidth
                disabled={hasNothingToSelect}
                onChange={(e) => {
                  handleChange(e);
                  const coveredObjectId = e.target.value;
                  // MUI fires onChange even when the same item is re-picked. For a party already
                  // marked not listed the fields below are its manual entry, so falling through
                  // would clear the contact and description the adjuster typed.
                  if (isMarkedNotListedOnPolicy && coveredObjectId === notListedOptionValue) {
                    return;
                  }
                  const coveredObject = coveredObjects.find((coveredObject) => coveredObject.id === coveredObjectId);

                  // Blank for the not-listed marker, which matches no policy object: the server
                  // rejects an FNOL carrying both the marker and an external id.
                  setFieldValue('covered_object_external_id', coveredObject?.external_id ?? '');

                  if (partyType === ENTITIES_TYPES.PROPERTY) {
                    setFieldValue('property_description', coveredObject?.display_name || '');
                    setFieldValue('display_name', coveredObject?.display_name || '');
                    setFieldValue('owner_contact', coveredObject?.contact);
                    setFieldValue('owner_contact_id', coveredObject?.contact_id || '');
                    setFieldValue('owner_contact_full_name', coveredObject?.contact_full_name || '');
                  } else {
                    setFieldValue('contact', coveredObject?.contact);
                    setFieldValue('contact_id', coveredObject?.contact_id || '');
                    setFieldValue('contact_full_name', coveredObject?.contact_full_name || '');
                  }
                }}
              >
                {hasNothingToSelect ? (
                  <MenuItem value="" disabled>
                    No eligible covered contacts available
                  </MenuItem>
                ) : (
                  coveredObjects.map((coveredObject) => (
                    <MenuItem key={coveredObject.id} value={coveredObject.id}>
                      {`${getCoveredObjectDisplayName(coveredObject)}${getManagementPreviewLabelSuffix(
                        policy,
                        incidentConfiguration,
                        coveredObject.id,
                        currencyFormatter
                      )}`}
                    </MenuItem>
                  ))
                )}
                {/* Kept out of coveredObjects: the map above resolves a display name and management
                    preview from the policy, which a synthetic entry has neither of. */}
                {showsNotListedOnPolicyOption && <MenuItem value={notListedOptionValue}>Not listed on policy</MenuItem>}
              </TextFieldFormik>
              {hasNothingToSelect && (
                <AlertBanner
                  withIcon
                  alertType={AlertBanner.ALERT_TYPES.WARNING}
                  note={`No eligible covered contact found on the associated policy. Add a ${coveredObjectDesc} to the policy first, then add it here.`}
                  className="mt-8"
                />
              )}
            </Grid>
          )}
          {linkedCoveredObject && coveredObjectShowInInvolvedFields.length > 0 && (
            <Grid item xs={12}>
              <CustomFieldsContextProvider customFieldsConfigurations={coveredObjectShowInInvolvedFields}>
                <Grid container spacing={1}>
                  {coveredObjectShowInInvolvedFields.map((field) => (
                    <Grid item xs={FULL_ROW_TYPES.includes(field?.type) ? 12 : 4} key={field.id}>
                      <ReadOnlyCoveredObjectField field={field} linkedCoveredObject={linkedCoveredObject} />
                    </Grid>
                  ))}
                </Grid>
              </CustomFieldsContextProvider>
            </Grid>
          )}
          {partyType === ENTITIES_TYPES.PERSON && (
            <>
              {/* Show contact picker whenever the covered-object dropdown does not supply the contact:
                  third parties, first parties without a policy / on a manual policy, unscheduled first
                  parties, or a first party marked as not listed on the policy. */}
              {showsManualCoveredObjectEntry && <PreDefinedField id="contact_id" fields={preDefinedFields} inline />}
              <PreDefinedField id="relationship_to_policyholder" fields={preDefinedFields} inline />
              <PreDefinedField id="attorney_contact_id" fields={preDefinedFields} inline />
              <PreDefinedField id="attorney_reference_number" fields={preDefinedFields} inline />
              <PreDefinedField id="representative_contact_id" fields={preDefinedFields} inline />
            </>
          )}
          {partyType === ENTITIES_TYPES.PROPERTY && (
            <>
              {shouldShowPropertyDescription && (
                <PreDefinedField id="property_description" fields={preDefinedFields} inline />
              )}
              <PreDefinedField id="owner_contact_id" fields={preDefinedFields} inline />
              <PreDefinedField id="attorney_contact_id" fields={preDefinedFields} inline />
              <PreDefinedField id="attorney_reference_number" fields={preDefinedFields} inline />
            </>
          )}
          <InvolvedInsurerFields preDefinedFields={preDefinedFields} />
          <PreDefinedField id="injury_date" fields={preDefinedFields} inline />
          {!isEmpty(configuredFields) && <ConfiguredFields values={values} customFields={configuredFields} inline />}
        </Grid>
      </InnerCard>
    </Grid>
  );
};

GeneralDetailsFragment.propTypes = {
  involvedParty: PropTypes.string,
  preDefinedFields: PropTypes.object.isRequired,
};

export { GeneralDetailsFragment };
